'use client'

import * as React from 'react'
import { CalendarIcon, ChevronDownIcon } from 'lucide-react'

import { Button, Calendar, Popover, PopoverContent, PopoverTrigger } from '@ui/web/components'
import { typographyVariants } from '@ui/web/lib'
import cn from '@config/tailwind/cn'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DateRangeValue {
  from: Date
  to: Date
}

type Preset = 'month' | 'quarter' | 'semester' | 'custom'

const PRESET_LABELS: Record<Preset, string> = {
  month: '1 bulan terakhir',
  quarter: '1 triwulan terakhir',
  semester: '1 semester terakhir',
  custom: 'Pilih rentang',
}

const PRESET_MONTHS: Record<Exclude<Preset, 'custom'>, number> = {
  month: 1,
  quarter: 3,
  semester: 6,
}

const PRESET_ORDER: Exclude<Preset, 'custom'>[] = ['month', 'quarter', 'semester']

/** Snap `from` to 00:00:00 and `to` to 23:59:59.999 local time so day boundaries are inclusive. */
export function normalizeDateRange(from: Date, to: Date): DateRangeValue {
  const start = new Date(from)
  start.setHours(0, 0, 0, 0)
  const end = new Date(to)
  end.setHours(23, 59, 59, 999)
  return { from: start, to: end }
}

/** Build a `{ from, to }` range that ends today and starts `months` ago, snapped to local day boundaries. */
function computePresetRange(months: number): DateRangeValue {
  const from = new Date()
  from.setMonth(from.getMonth() - months)
  return normalizeDateRange(from, new Date())
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface DateRangeSelectProps {
  /** Controlled resolved range. The parent owns the value so queries can refetch on change. */
  value?: DateRangeValue
  onChange: (range: DateRangeValue) => void
  /** Which preset to label the trigger with on first mount. */
  defaultPreset?: Preset
  size?: 'sm' | 'default'
  className?: string
}

export function DateRangeSelect({
  value,
  onChange,
  defaultPreset = 'month',
  size = 'sm',
  className,
}: DateRangeSelectProps) {
  const [open, setOpen] = React.useState(false)
  const [view, setView] = React.useState<'presets' | 'calendar'>('presets')
  const [preset, setPreset] = React.useState<Preset>(defaultPreset)
  const [draftRange, setDraftRange] = React.useState<{ from?: Date; to?: Date } | undefined>(value)

  const selectPreset = (p: Exclude<Preset, 'custom'>) => {
    setPreset(p)
    onChange(computePresetRange(PRESET_MONTHS[p]))
    setOpen(false)
  }

  const applyCustomRange = () => {
    if (!draftRange?.from || !draftRange?.to) return
    setPreset('custom')
    onChange(normalizeDateRange(draftRange.from, draftRange.to))
    setOpen(false)
    setView('presets')
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) setView('presets')
      }}
    >
      <PopoverTrigger
        data-size={size}
        className={cn(
          typographyVariants({ variant: 'body-lg' }),
          'srd:border-outline srd:text-neutral-black srd:focus-visible:border-blue-100 srd:focus-visible:ring-blue-20 srd:flex srd:w-full srd:min-w-0 srd:items-center srd:justify-between srd:gap-1.5 srd:rounded-lg srd:data-[size=sm]:rounded-[min(var(--radius-md),10px)] srd:border srd:bg-neutral-white srd:py-2 srd:pr-2 srd:pl-2.5 srd:whitespace-nowrap srd:transition-colors srd:outline-none srd:select-none srd:focus-visible:ring-[3px] srd:disabled:cursor-not-allowed srd:disabled:bg-grey-10 srd:disabled:opacity-50 srd:data-[size=default]:h-12 srd:data-[size=sm]:h-8',
          className,
        )}
      >
        <span className='srd:truncate'>{PRESET_LABELS[preset]}</span>
        <ChevronDownIcon className='srd:text-blue-100 srd:size-4 srd:shrink-0 srd:pointer-events-none' />
      </PopoverTrigger>
      <PopoverContent align='start' sideOffset={6} className='srd:p-1'>
        {view === 'presets' ? (
          <div className='srd:flex srd:min-w-[200px] srd:flex-col'>
            {PRESET_ORDER.map((p) => (
              <button
                key={p}
                type='button'
                onClick={() => selectPreset(p)}
                className={cn(
                  typographyVariants({ variant: 'body-md' }),
                  'srd:flex srd:items-center srd:rounded-md srd:px-3 srd:py-2 srd:text-left srd:text-neutral-black',
                  'srd:hover:bg-grey-10',
                  preset === p && 'srd:bg-blue-10',
                )}
              >
                {PRESET_LABELS[p]}
              </button>
            ))}
            <div className='srd:my-1 srd:border-t srd:border-grey-30' />
            <button
              type='button'
              onClick={() => setView('calendar')}
              className={cn(
                typographyVariants({ variant: 'body-md' }),
                'srd:flex srd:items-center srd:gap-2 srd:rounded-md srd:px-3 srd:py-2 srd:text-left srd:text-neutral-black',
                'srd:hover:bg-grey-10',
                preset === 'custom' && 'srd:bg-blue-10',
              )}
            >
              <CalendarIcon className='srd:text-blue-100 srd:size-4 srd:shrink-0' />
              {PRESET_LABELS.custom}
            </button>
          </div>
        ) : (
          <div className='srd:flex srd:flex-col srd:gap-2 srd:p-1'>
            <Calendar
              mode='range'
              numberOfMonths={1}
              selected={draftRange as never}
              onSelect={(range) => setDraftRange(range as { from?: Date; to?: Date } | undefined)}
              className='srd:w-full'
            />
            <div className='srd:flex srd:items-center srd:justify-end srd:gap-2'>
              <Button variant='ghost' size='sm' onClick={() => setView('presets')}>
                Kembali
              </Button>
              <Button size='sm' onClick={applyCustomRange} disabled={!draftRange?.from || !draftRange?.to}>
                Terapkan
              </Button>
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
